import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { vuexOidcCreateRouterMiddleware } from "vuex-oidc";
import store from "../store";
import Home from "../views/Home.vue";
import Dashboard from "../views/Dashboard.vue";
import Applications from "../views/Applications.vue";
import Application from "../views/Application.vue";
import Certifications from "../views/Certifications.vue";
import Certification from "../views/Certification.vue";
import Disbursements from "../views/Disbursements.vue";
import Loans from "../views/Loans.vue";
import Loan from "../views/Loan.vue";
import Administration from "../views/Administration.vue";
import Lenders from "../views/Lenders.vue";
import Bureaus from "../views/Bureaus.vue";
import Batches from "../views/Batches.vue";
import Programs from "../views/Programs.vue";
import Pricing from "../views/Pricing.vue";
import Settings from "../views/Settings.vue";
import Profile from "../views/Profile.vue";
import Reports from "../views/Reports.vue";
import ReportViewer from "../views/ReportViewer.vue";
import NoAccess from "../views/NoAccess.vue";
import BatchEdit from "../components/disbursements/BatchEdit.vue";
import DisbursementBatchDetails from "../components/batches/BatchDetails.vue";
import PayoffBatchDetails from "../components/payoff-batches/BatchDetails.vue";
import OidcCallback from "../views/OidcCallback.vue";
import OidcCallbackError from "../views/OidcCallbackError.vue";
import PageNotFound from "../views/PageNotFound.vue";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { Products } from "@/types/types";
import { usePermissionsStore } from "@/store/store/permissionsStore";
import { Permissions, PermissionLevels } from "@/models/permissions";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    meta: {
      title: `Home`,
    },
    component: Home,
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    meta: {
      title: `Dashboard`,
    },
    component: Dashboard,
  },
  {
    path: "/applications",
    name: "Applications",
    meta: {
      title: "Applications",
      permission: Permissions.Applications,
    },
    component: Applications,
  },
  {
    path: "/applications/:referenceId",
    name: "Application",
    meta: {
      title: `Application`,
      permission: Permissions.Applications,
    },
    component: Application,
    beforeEnter: (to, from, next) => {
      const applicationStore = useApplicationStore();

      applicationStore
        .getApplicationRoute(to.params.referenceId as string)
        .then(() => {
          const productTypeId =
            applicationStore.applicationMetadata?.productTypeId;
          if (
            productTypeId === Products.LOC ||
            productTypeId === Products.REFI ||
            productTypeId === Products.PEL
          ) {
            to.matched[0].components = { default: Application };
            next();
          } else {
            // Handle other product types
            next(false);
          }
        })
        .catch((error) => {
          console.error("getApplication", error);
          next(false);
        });
    },
  },
  {
    path: "/v2/applications/:referenceId",
    name: "ApplicationV2",
    meta: {
      permission: Permissions.Applications,
    },
    component: Application,
    alias: "/applications/:referenceId",
  },
  {
    path: "/certifications",
    name: "Certifications",
    meta: {
      title: `Certifications`,
    },
    component: Certifications,
  },
  {
    path: "/certifications/:id",
    name: "Certification",
    meta: {
      title: `Certification`,
      breadcrumb: "Certifications",
      breadcrumbDetails: {
        label: "Certifications",
        to: { name: "Certifications", path: "/certifications" },
      },
    },
    component: Certification,
  },
  {
    path: "/disbursements",
    name: "Disbursements",
    meta: {
      title: `Disbursements`,
    },
    component: Disbursements,
  },
  {
    path: "/disbursements/:id",
    name: "Disbursement",
    meta: {
      title: `Disbursement`,
      breadcrumb: "Disbursements",
      breadcrumbDetails: {
        label: "Disbursements",
        to: { name: "Disbursements", path: "/disbursements" },
      },
    },
    component: Certification,
  },

  {
    path: "/loans",
    name: "Loans",
    meta: {
      title: `Loans`,
      permission: Permissions.Loans,
    },
    component: Loans,
  },
  {
    path: "/batches",
    name: "Batches",
    meta: {
      title: `Disbursement Batches`,
    },
    component: Batches,
  },
  {
    path: "/disbursementbatches/:batchNumber", // todo: remove this and its components once loan svc migrates to cert/disb
    name: "BatchEdit",
    meta: {
      title: `Batch Edit`,
    },
    component: BatchEdit,
  },
  {
    path: "/payoff-batches/:batchId",
    name: "PayoffBatchDetails",
    meta: {
      title: "Payoff Batch Details",
    },
    component: PayoffBatchDetails,
  },
  {
    path: "/disbursement-batches/:batchId",
    name: "DisbursementBatchDetails",
    meta: {
      title: "Disbursement Batch Details",
      breadcrumb: "Disbursement Batches",
      breadcrumbDetails: {
        label: "Disbursement Batches",
        to: { name: "Batches", path: "/batches" },
        isActive: false,
      },
    },
    component: DisbursementBatchDetails,
  },
  {
    path: "/loans/:referenceId",
    name: "Loan",
    meta: {
      title: "Loan",
      breadcrumb: "Loans",
      permission: Permissions.Loans,
    },
    component: Loan,
  },
  {
    path: "/administration",
    name: "Administration",
    meta: {
      title: `Administration`,
    },
    component: Administration,
  },
  {
    path: "/lenders",
    name: "Lenders",
    meta: {
      title: `Lenders`,
    },
    component: Lenders,
  },
  {
    path: "/bureaus",
    name: "Bureaus",
    meta: {
      title: `Bureaus`,
    },
    component: Bureaus,
  },
  {
    path: "/programs",
    name: "Programs",
    meta: {
      title: `Programs`,
    },
    component: Programs,
  },
  {
    path: "/pricing",
    name: "Pricing",
    meta: {
      title: `Pricing`,
    },
    component: Pricing,
  },
  {
    path: "/settings",
    name: "Settings",
    meta: {
      title: `Settings`,
    },
    component: Settings,
  },
  {
    path: "/profile",
    name: "Profile",
    meta: {
      title: `Profile`,
    },
    component: Profile,
  },
  {
    path: "/loggedout",
    name: "loggedout",
    component: () => import("../views/LoggedOut.vue"),
    meta: {
      isPublic: true,
      title: "Logged Out",
    },
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("../views/Logout.vue"),
    meta: {
      isPublic: true,
      title: "Log Out",
    },
  },
  {
    // needs to match redirectUri in oidcSettings
    path: "/oidc-callback",
    name: "oidcCallback",
    component: OidcCallback,
    meta: {
      isPublic: true,
      title: "Redirecting...",
    },
  },
  {
    // needs to match redirect_uri in oidcSettings
    path: "/oidc-callback-error",
    name: "oidcCallbackError",
    component: OidcCallbackError,
    meta: {
      isPublic: true,
      title: "Error",
    },
  },
  {
    path: "/page-not-found",
    name: "PageNotFound",
    component: PageNotFound,
    meta: {
      isPublic: true,
      title: "Page Not Found",
    },
  },
  {
    path: "/reports",
    name: "Reports",
    meta: {
      title: `Reports`,
      breadcrumb: "Data Warehouse",
      permission: Permissions.Reports,
      breadcrumbDetails: {
        to: { name: "Reports", path: "/reports" },
        isActive: true,
      },
    },
    component: Reports,
  },
  {
    path: "/no-access",
    name: "NoAccess",
    meta: {
      title: "No Access",
    },
    component: NoAccess,
  },
  {
    path: "/reports/view/:id",
    name: "ReportViewer",
    meta: {
      title: `Report`,
    },
    component: ReportViewer,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return false;
  },
});

router.beforeEach(vuexOidcCreateRouterMiddleware(store, "oidcStore"));

// Permission guard: routes can declare meta.permission to require a permission for
// entry. Blocks direct-URL/API-less access to areas the nav already hides (e.g. a
// ReportViewer navigating to /applications), redirecting them to the Data Warehouse.
router.beforeEach(async (to, from, next) => {
  const requiredPermission = to.meta.permission as Permissions | undefined;
  if (requiredPermission === undefined) {
    next();
    return;
  }

  const permissionsStore = usePermissionsStore();
  const roles = permissionsStore.currentUser.roles;
  if (!Array.isArray(roles) || roles.length === 0) {
    await permissionsStore.loadUserRolesFromStorage();
  }

  if (permissionsStore.hasAccess(requiredPermission, PermissionLevels.Read)) {
    next();
  } else {
    next({ path: "/" });
  }
});

export default router;
